/**
 * Processing Status Component
 * Shows progress during chat processing (searching, analyzing, generating)
 */

import { Loader2, Search, Brain, Globe, FileText } from 'lucide-react';

export interface ProcessingStep {
  step: 'searching' | 'analyzing' | 'generating' | 'complete';
  message: string;
  source?: 'ai-search' | 'graph-api' | 'web-search';
}

interface ProcessingStatusProps {
  step: ProcessingStep;
}

export function ProcessingStatus({ step }: ProcessingStatusProps) {
  const getIcon = () => {
    switch (step.step) {
      case 'searching':
        if (step.source === 'ai-search') {
          return <FileText className="h-4 w-4 text-stellar-primary" />;
        }
        if (step.source === 'web-search') {
          return <Globe className="h-4 w-4 text-success" />;
        }
        return <Search className="h-4 w-4 text-stellar-primary" />;
      case 'analyzing':
        return <Brain className="h-4 w-4 text-text-secondary" />;
      case 'generating':
        return <Loader2 className="h-4 w-4 text-stellar-primary animate-spin" />;
      default:
        return <Loader2 className="h-4 w-4 text-stellar-primary animate-spin" />;
    }
  };

  const getBackgroundColor = () => {
    switch (step.step) {
      case 'searching':
        if (step.source === 'ai-search') return 'bg-bg-tertiary';
        if (step.source === 'web-search') return 'bg-success-light';
        return 'bg-stellar-primary-light/10';
      case 'analyzing':
        return 'bg-bg-tertiary';
      case 'generating':
        return 'bg-stellar-primary-light/10';
      default:
        return 'bg-bg-secondary';
    }
  };

  const getTextColor = () => {
    switch (step.step) {
      case 'searching':
        if (step.source === 'ai-search') return 'text-stellar-primary-dark';
        if (step.source === 'web-search') return 'text-success';
        return 'text-stellar-primary';
      case 'analyzing':
        return 'text-purple-500';
      case 'generating':
        return 'text-stellar-primary';
      default:
        return 'text-text-primary';
    }
  };

  return (
    <div
      className={`flex items-center gap-2 rounded-lg px-4 py-3 ${getBackgroundColor()}`}
      role="status"
      aria-live="polite"
    >
      {getIcon()}
      <span className={`text-sm font-medium ${getTextColor()}`}>
        {step.message}
      </span>
    </div>
  );
}
